import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Area,
  ResponsiveContainer,
} from "recharts";
import colors from "../styles/_settings.scss";
import { Loader2 } from "lucide-react";

const CoinChart = ({ coinId, coinName }) => {
  const [coinData, setCoinData] = useState([]);
  const [duration, setDuration] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const headerData = [
    [1, "1D"],
    [3, "3D"],
    [7, "7D"],
    [30, "1M"],
    [91, "3M"],
    [181, "6M"],
    [365, "1Y"],
    [3000, "MAX"],
  ];

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(false);

    axios
      .get(
        `https://api.coingecko.com/api/v3/coins/${coinId}/market_chart?vs_currency=usd&days=${duration}${
          duration > 32 ? "&interval=daily" : ""
        }`
      )
      .then((res) => {
        if (!isMounted) return;
        if (res.data && res.data.prices) {
          const dataArray = res.data.prices.map((item) => {
            const price = item[1];
            return {
              date: new Date(item[0]).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              }),
              price: price < 1 ? Number(price.toFixed(6)) : Number(price.toFixed(2)),
            };
          });
          setCoinData(dataArray);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.warn("Error fetching chart data from CoinGecko:", err);
        setError(true);
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [coinId, duration]);

  const CustomChartTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-chart-tooltip">
          <p className="tooltip-date">{data.date}</p>
          <p className="tooltip-price">${data.price.toLocaleString()}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="coin-chart-wrapper">
      <div className="timeframe-bar">
        <span className="chart-title">Price History</span>
        <div className="btn-container">
          {headerData.map((radio) => (
            <button
              key={radio[0]}
              onClick={() => setDuration(radio[0])}
              className={`timeframe-btn ${radio[0] === duration ? "active" : ""}`}
            >
              {radio[1]}
            </button>
          ))}
        </div>
      </div>

      <div className="chart-body">
        {loading ? (
          <div className="chart-status-box">
            <Loader2 size={24} className="spin-loader" />
            <span>Loading chart...</span>
          </div>
        ) : error || coinData.length === 0 ? (
          <div className="chart-status-box">
            <span>Chart unavailable due to CoinGecko API rate limits.</span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={coinData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={colors.color1 || "#00f2fe"} stopOpacity={0.4} />
                  <stop offset="95%" stopColor={colors.color1 || "#00f2fe"} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis
                domain={["auto", "auto"]}
                stroke="#64748b"
                fontSize={11}
                tickLine={false}
                tickFormatter={(val) => `$${val >= 1000 ? (val / 1000).toFixed(0) + "k" : val}`}
              />
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
              <Tooltip content={<CustomChartTooltip />} />
              <Area
                type="monotone"
                dataKey="price"
                stroke={colors.color1 || "#00f2fe"}
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#chartGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default CoinChart;
